import {Mesh, PerspectiveCamera, Vector3} from "three";
import React, {MutableRefObject, useCallback, useEffect, useRef, useState} from "react";
import {useThree} from "@react-three/fiber";
import {OrbitControls} from "@react-three/drei";

import {StlObject} from "./StlObject";
import {DEFAULT_GRID_SIZE, DEFAULT_TARGET_SIZE} from "./ViewerCanvas";
import {GridCornerMarkers} from "./GridCornerMarkers";
import {ViewerLighting} from "./ViewerLighting";
import {CursorCoordinatePlane} from "./CursorCoordinatePlane";

// Adjust both the camera's focal length and its position such that the object appears
// roughly the same size on-screen, but the perspective changes to give the illusion
// of a smaller or larger object. A scale larger than one means the field of view
// will get smaller causing the object to appear smaller.
function adjustFov(camera: PerspectiveCamera, scale: number): void {
  camera.setFocalLength(camera.getFocalLength() * scale);
  camera.position.multiplyScalar(scale);
}

const initialCameraPosition: Vector3 = new Vector3(0, 7, 14);

export const ViewerScene: React.FC<{
  url?: string;
  getViewerBounds?: () => (DOMRect | undefined);
}> = ({url, getViewerBounds}) => {

  const [gridRadius, setGridRadius] = useState<number>(1);

  const camera = useThree().camera as PerspectiveCamera;
  const mesh   = useRef() as MutableRefObject<Mesh>;

  const cameraPositionInitialized = useRef<boolean>(false);

  const initializeCameraPosition = useCallback(() => {
    if (cameraPositionInitialized.current) return;
    camera.position.x                 = initialCameraPosition.x;
    camera.position.y                 = initialCameraPosition.y;
    camera.position.z                 = initialCameraPosition.z;
    cameraPositionInitialized.current = true;
  }, [camera]);

  useEffect(initializeCameraPosition, [initializeCameraPosition]);

  const withObjectSize = useCallback((size: number) => {

    // Once we know the size of the object (where the size is the length along the "longest"
    // dimension of the bounding box), we'll adjust the focal length of the camera to create
    // the illusion of a larger or smaller object
    const currentFocalLength = camera.getFocalLength();

    const targetFocalLength = 100 / Math.log(size);

    const focalLengthAdjustment = targetFocalLength / currentFocalLength;

    initializeCameraPosition();
    adjustFov(camera, focalLengthAdjustment);

    setGridRadius(size / 2 * (DEFAULT_GRID_SIZE / DEFAULT_TARGET_SIZE));

  }, [camera, initializeCameraPosition]);

  return <>
    <OrbitControls makeDefault/>
    <GridCornerMarkers gridRadius={gridRadius}/>
    <CursorCoordinatePlane
      gridRadius={gridRadius / (DEFAULT_GRID_SIZE / 2)}
      getViewerBounds={getViewerBounds}
    />
    <gridHelper/>
    <axesHelper/>
    {url
      ? <StlObject
        key={url}
        url={url}
        scaleTo={DEFAULT_TARGET_SIZE}
        ref={mesh}
        withObjectSize={withObjectSize}
      />
      : null}
    <ViewerLighting/>
  </>;
};